import { Badge, Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import {
  MCP_TOKEN_DEFAULT_EXPIRY_DAYS,
  MCP_TOKEN_EXPIRY_PRESET_DAYS,
  MCP_TOKEN_MAX_EXPIRY_DAYS,
  type IssueMcpTokenResult,
  type McpTokenSummary,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { app } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import { useDistricts } from '../districts-admin';
import { IssuedTokenModal } from './IssuedTokenModal';
import { useMcpTokens } from './useMcpTokens';

/** MCP サーバーの endpoint URL (Cloud Functions 直 URL)。 */
function resolveEndpointUrl(): string {
  const projectId = app.options.projectId ?? '<project>';
  return `https://asia-northeast1-${projectId}.cloudfunctions.net/mcpServer`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

type TokenStatus = 'active' | 'expired' | 'revoked';
function tokenStatus(t: McpTokenSummary): TokenStatus {
  if (t.revokedAt) return 'revoked';
  if (t.expiresAt && new Date(t.expiresAt).getTime() <= Date.now()) return 'expired';
  return 'active';
}

/**
 * 外部 AI 連携 (MCP) トークン管理画面。
 *
 * master / 地区管理者が、使い慣れた AI チャット (Claude / ChatGPT / Gemini) から自地区データに
 * アクセスするためのベアラートークンを発行 / 一覧 / 失効する。raw token は発行時に1回だけ表示。
 * 認可は callable 側で強制 (master = 任意地区 / 地区管理者 = 自地区のみ)。
 */
export function McpTokensPage() {
  const { state: auth } = useAdminAuth();
  const { toast } = useToast();
  const districtsState = useDistricts();
  const { state, refresh, issueToken, revokeToken, updateExpiry } = useMcpTokens();

  const [label, setLabel] = useState('');
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<number>(MCP_TOKEN_DEFAULT_EXPIRY_DAYS);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<IssueMcpTokenResult | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<McpTokenSummary | null>(null);
  /** 期限変更: トークンごとの選択日数 / 処理中のトークン。 */
  const [renewDays, setRenewDays] = useState<Record<string, number>>({});
  const [renewingId, setRenewingId] = useState<string | null>(null);

  const endpointUrl = useMemo(() => resolveEndpointUrl(), []);
  const isAdmin = auth.role === 'master' || auth.role === 'district';

  // 地区 ID → 名称マップ (一覧の scope 表示用)。
  const districtNameById = useMemo(() => {
    const m = new Map<string, string>();
    if (districtsState.status === 'ready') {
      for (const d of districtsState.items) m.set(d.id, d.name);
    }
    return m;
  }, [districtsState]);

  const selectableDistricts = districtsState.status === 'ready' ? districtsState.items : [];

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin, refresh]);

  if (auth.ready && !isAdmin) {
    return (
      <Card padding="md">
        <p className="text-sm text-slate-700">
          この画面はマスター管理者・地区管理者のみ利用できます。
        </p>
      </Card>
    );
  }

  const toggleDistrict = (id: string) => {
    setSelectedDistricts((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id],
    );
  };

  const onIssue = async (e: FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      toast('error', 'ラベルを入力してください');
      return;
    }
    if (selectedDistricts.length === 0) {
      toast('error', '対象の地区を 1 つ以上選択してください');
      return;
    }
    setIssuing(true);
    try {
      const result = await issueToken({
        label: label.trim(),
        scopeDistrictIds: selectedDistricts,
        expiresInDays,
      });
      setIssued(result);
      setLabel('');
      setSelectedDistricts([]);
      setExpiresInDays(MCP_TOKEN_DEFAULT_EXPIRY_DAYS);
      await refresh();
    } catch (err) {
      toast('error', `発行に失敗しました: ${(err as Error).message}`);
    } finally {
      setIssuing(false);
    }
  };

  const confirmRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revokeToken({ tokenId: pendingRevoke.tokenId });
      toast('success', `「${pendingRevoke.label}」を失効しました`);
      setPendingRevoke(null);
      await refresh();
    } catch (err) {
      toast('error', `失効に失敗しました: ${(err as Error).message}`);
    }
  };

  const onRenew = async (t: McpTokenSummary) => {
    const days = renewDays[t.tokenId] ?? MCP_TOKEN_DEFAULT_EXPIRY_DAYS;
    setRenewingId(t.tokenId);
    try {
      const res = await updateExpiry({ tokenId: t.tokenId, expiresInDays: days });
      toast('success', `「${t.label}」の期限を ${formatDateTime(res.expiresAt)} に変更しました`);
      await refresh();
    } catch (err) {
      toast('error', `期限の変更に失敗しました: ${(err as Error).message}`);
    } finally {
      setRenewingId(null);
    }
  };

  return (
    <>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">外部 AI 連携（MCP）</h1>
        <p className="mt-1 text-sm text-slate-500 leading-relaxed">
          Claude・ChatGPT・Gemini などの AI チャットから、担当地区の投稿・AI解析・気づき・処方箋を
          テキストで問い合わせ・分析できるアクセストークンを発行します。写真は含まれません。トークンは
          有効期限（最大{MCP_TOKEN_MAX_EXPIRY_DAYS}
          日）付きで、発行後も期限を変更でき、いつでも失効できます。
        </p>
        <p className="mt-2 text-xs text-slate-500">
          接続先（endpoint）: <code className="break-all">{endpointUrl}</code>
        </p>
      </header>

      {/* 発行フォーム */}
      <Card padding="md" className="mb-6">
        <h2 className="text-base font-semibold text-slate-900 mb-3">トークンを発行</h2>
        <form onSubmit={(e) => void onIssue(e)} className="grid grid-cols-1 gap-4">
          <label className="grid grid-cols-1 gap-1">
            <span className="text-xs font-semibold text-slate-700">ラベル（用途の識別名）</span>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="例：箱根景観の分析（研究者A）"
              className="rounded border border-slate-300 px-3 py-2 text-sm"
              maxLength={120}
            />
          </label>

          <fieldset className="grid grid-cols-1 gap-1">
            <legend className="text-xs font-semibold text-slate-700 mb-1">対象の地区</legend>
            {districtsState.status === 'loading' && (
              <p className="text-sm text-slate-500">地区を読み込み中です…</p>
            )}
            {districtsState.status === 'error' && (
              <p className="text-sm text-rose-600">地区の読み込みに失敗しました。</p>
            )}
            {districtsState.status === 'ready' && selectableDistricts.length === 0 && (
              <p className="text-sm text-slate-500">選択できる地区がありません。</p>
            )}
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {selectableDistricts.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-sm text-slate-800">
                  <input
                    type="checkbox"
                    checked={selectedDistricts.includes(d.id)}
                    onChange={() => toggleDistrict(d.id)}
                  />
                  {d.name}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="grid grid-cols-1 gap-1 max-w-[12rem]">
            <span className="text-xs font-semibold text-slate-700">有効期限</span>
            <select
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(Number(e.target.value))}
              className="rounded border border-slate-300 px-3 py-2 text-sm"
            >
              {MCP_TOKEN_EXPIRY_PRESET_DAYS.map((d) => (
                <option key={d} value={d}>
                  {d} 日
                </option>
              ))}
            </select>
          </label>

          <div>
            <Button type="submit" disabled={issuing}>
              {issuing ? '発行中…' : 'トークンを発行'}
            </Button>
          </div>
        </form>
      </Card>

      {/* 一覧 */}
      <Card padding="md">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-900">発行済みトークン</h2>
          <Button variant="secondary" size="sm" onClick={() => void refresh()}>
            再読み込み
          </Button>
        </div>

        {state.status === 'loading' && <p className="text-sm text-slate-500">読み込み中です…</p>}
        {state.status === 'error' && (
          <p className="text-sm text-rose-600">一覧の取得に失敗しました: {state.error.message}</p>
        )}
        {state.status === 'ready' && state.tokens.length === 0 && (
          <p className="text-sm text-slate-500">発行済みのトークンはありません。</p>
        )}
        {state.status === 'ready' && state.tokens.length > 0 && (
          <ul className="grid grid-cols-1 gap-2">
            {state.tokens.map((t) => {
              const status = tokenStatus(t);
              const scopeNames = t.scopeDistrictIds
                .map((id) => districtNameById.get(id) ?? id)
                .join('、');
              return (
                <li key={t.tokenId} className="rounded border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 truncate">{t.label}</p>
                        {status === 'active' && (
                          <Badge variant="success" size="sm">
                            有効
                          </Badge>
                        )}
                        {status === 'expired' && (
                          <Badge variant="default" size="sm">
                            期限切れ
                          </Badge>
                        )}
                        {status === 'revoked' && (
                          <Badge variant="danger" size="sm">
                            失効済み
                          </Badge>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-slate-600">対象地区：{scopeNames || '—'}</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">
                        期限：{formatDateTime(t.expiresAt)} ／ 最終利用：
                        {formatDateTime(t.lastUsedAt)}
                      </p>
                      {status !== 'revoked' && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <label className="flex items-center gap-1 text-xs text-slate-700">
                            期限を今日から
                            <select
                              value={renewDays[t.tokenId] ?? MCP_TOKEN_DEFAULT_EXPIRY_DAYS}
                              onChange={(e) =>
                                setRenewDays((prev) => ({
                                  ...prev,
                                  [t.tokenId]: Number(e.target.value),
                                }))
                              }
                              className="rounded border border-slate-300 px-2 py-1 text-xs"
                            >
                              {MCP_TOKEN_EXPIRY_PRESET_DAYS.map((d) => (
                                <option key={d} value={d}>
                                  {d} 日
                                </option>
                              ))}
                            </select>
                            に
                          </label>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={renewingId === t.tokenId}
                            onClick={() => void onRenew(t)}
                          >
                            {renewingId === t.tokenId ? '変更中…' : '期限を変更'}
                          </Button>
                        </div>
                      )}
                    </div>
                    {status !== 'revoked' && (
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        onClick={() => setPendingRevoke(t)}
                      >
                        失効
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {issued && (
        <IssuedTokenModal
          result={issued}
          endpointUrl={endpointUrl}
          onClose={() => setIssued(null)}
        />
      )}

      <ConfirmDialog
        open={pendingRevoke !== null}
        intent="danger"
        title="トークンを失効"
        message={
          pendingRevoke
            ? `「${pendingRevoke.label}」を失効します。このトークンを使った連携は即座に使えなくなります。よろしいですか？`
            : ''
        }
        confirmLabel="失効する"
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setPendingRevoke(null)}
      />
    </>
  );
}
